import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    ShieldCheck,
    LogOut,
    ArrowLeft,
    ChevronRight,
    Sparkles,
    History,
    Terminal,
    BookOpen,
    ShoppingCart
} from 'lucide-react';
import { logout } from '../../redux/slices/userSlice';
import UserAvatar from '../Common/UserAvatar';

const AdminSidebar = ({ activeView = 'admin', setActiveView, isMobileOpen, setIsMobileOpen }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.user);

    const menuItems = [
        {
            id: 'admin',
            label: 'Overview',
            icon: LayoutDashboard,
            description: 'System health & metrics',
        },
        {
            id: 'users',
            label: 'User Base',
            icon: Users,
            description: 'Account management',
        },
        {
            id: 'loginlogs',
            label: 'Login History',
            icon: History,
            description: 'Authentication telemetry',
        },
        {
            id: 'systemlogs',
            label: 'System Logs',
            icon: Terminal,
            description: 'Real-time server activity',
        },
        {
            id: 'formulas',
            label: 'Formula Lab',
            icon: Sparkles,
            description: 'Zoning & logic repository',
        },
        {
            id: 'zoningdetails',
            label: 'Zoning Glossary',
            icon: BookOpen,
            description: 'District definitions & uses',
        },
        {
            id: 'subscriptionsettings',
            label: 'Subscription Settings',
            icon: ShoppingCart,
            description: 'Toggle plan availability',
        },
        {
            id: 'ssl',
            label: 'SSL Status',
            icon: ShieldCheck,
            description: 'Security & Certifications',
        },
    ];

    const handleLogout = () => {
        dispatch(logout());
        window.location.href = '/';
    };

    return (
        <>
            {/* Mobile Overlay */}
            {isMobileOpen && (
                <div
                    className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden"
                    onClick={() => setIsMobileOpen(false)}
                />
            )}

            {/* Fixed Vertical Sidebar */}
            <aside
                className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-72 bg-[#FBFBFC] border-r border-slate-200/60 transform transition-all duration-500 ease-[cubic-bezier(0.33,1,0.68,1)] ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
            >
                <div className="h-full flex flex-col">

                    {/* Professional Brand Header */}
                    <div className="h-24 px-8 flex items-center border-b border-slate-200/40">
                        <div className="flex items-center space-x-3.5 group cursor-pointer" onClick={() => navigate('/')}>
                            <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center shadow-xl shadow-slate-200 group-hover:scale-105 transition-transform duration-300">
                                <ShieldCheck className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-lg font-black text-slate-900 tracking-tight">Admin<span className="text-indigo-600">HQ</span></span>
                                <div className="flex items-center space-x-1 mt-0.5">
                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                                    <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Enterprise v2.4</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Precision Navigation Menu */}
                    <nav className="flex-1 px-4 py-8 space-y-1 overflow-y-auto no-scrollbar">
                        <p className="px-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.25em] mb-6">Management</p>
                        {menuItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = activeView === item.id;

                            return (
                                <button
                                    key={item.id}
                                    onClick={() => {
                                        if (setActiveView) setActiveView(item.id);
                                        setIsMobileOpen(false);
                                    }}
                                    className={`w-full flex items-center justify-between px-5 py-3.5 rounded-2xl transition-all duration-300 group ${isActive
                                        ? 'bg-white text-slate-900 shadow-sm border border-slate-200/60'
                                        : 'text-slate-500 hover:bg-slate-100/50 hover:text-slate-900'
                                        }`}
                                >
                                    <div className="flex items-center space-x-4">
                                        <div className={`p-2 rounded-xl transition-all duration-300 ${isActive ? 'bg-indigo-600 shadow-lg shadow-indigo-100' : 'bg-slate-100 group-hover:bg-white'}`}>
                                            <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-slate-600'}`} />
                                        </div>
                                        <div className="text-left">
                                            <p className="text-sm font-bold tracking-tight">{item.label}</p>
                                        </div>
                                    </div>
                                    {isActive && <ChevronRight className="w-3.5 h-3.5 text-slate-300" />}
                                </button>
                            );
                        })}

                        <div className="pt-10">
                            <p className="px-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.25em] mb-6">System</p>
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="w-full flex items-center justify-between px-5 py-3.5 rounded-2xl text-slate-500 hover:bg-slate-100/50 hover:text-slate-900 transition-all duration-300 group"
                            >
                                <div className="flex items-center space-x-4">
                                    <div className="p-2 rounded-xl bg-slate-100 group-hover:bg-white transition-all">
                                        <ArrowLeft className="w-4 h-4 text-slate-400" />
                                    </div>
                                    <span className="text-sm font-bold tracking-tight">Return to App</span>
                                </div>
                            </button>
                        </div>
                    </nav>

                    {/* High-Standard User Profile (Apple/Google Style) */}
                    <div className="p-6 border-t border-slate-200/40 bg-white/50">
                        <div className="flex items-center justify-between p-3 rounded-2xl bg-[#F8FAFC] border border-slate-200/60 group cursor-pointer hover:shadow-md transition-all duration-300">
                            <div className="flex items-center space-x-3">
                                <UserAvatar 
                                    user={user} 
                                    size="md" 
                                    shape="square"
                                    avatarClassName="rounded-xl shadow-indigo-100"
                                />
                                <div className="flex flex-col min-w-0">
                                    <p className="text-[11px] font-black text-slate-900 truncate leading-none mb-1">{user?.full_name || 'Admin'}</p>
                                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Platform Lead</span>
                                </div>
                            </div>
                            <button
                                onClick={handleLogout}
                                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                                <LogOut className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="mt-4 flex items-center justify-between px-2">
                            <div className="flex items-center space-x-1.5">
                                <Sparkles className="w-3 h-3 text-indigo-500" />
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Sync active</span>
                            </div>
                            <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]"></div>
                        </div>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default AdminSidebar;
